import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  Video,
  MapPin,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  FileText,
  ShieldCheck,
  Plus,
  Trash2,
  X,
  Loader2,
  Info,
} from "lucide-react";
import {
  listApplicationInterviews,
  createApplicationInterview,
  declineInterview,
  submitInterviewFeedback,
} from "@/lib/recruitmentApi";
import { getApiErrorMessage, logAppError } from "@/lib/api";

const TIMEZONES = [
  { value: "Europe/Paris", label: "Europe/Paris (UTC+1 / UTC+2)" },
  { value: "Africa/Casablanca", label: "Africa/Casablanca (UTC+0 / UTC+1)" },
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New_York (EST/EDT)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST UTC+4)" },
];

const INTERVIEW_TYPES = [
  { value: "rh", label: "Entretien RH" },
  { value: "technique", label: "Entretien Technique" },
  { value: "client", label: "Entretien Client" },
  { value: "final", label: "Entretien Final / Direction" },
];

const RATINGS = [
  { value: "tres_favorable", label: "Très favorable", color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  { value: "favorable", label: "Favorable", color: "bg-blue-50 text-blue-700 border-blue-300" },
  { value: "reserve", label: "Réservé", color: "bg-amber-50 text-amber-700 border-amber-300" },
  { value: "defavorable", label: "Défavorable", color: "bg-rose-50 text-rose-700 border-rose-300" },
];

export default function InterviewsWorkflowModal({
  isOpen,
  onClose,
  applicationId,
  candidateId,
  needId,
  onUpdated,
}) {
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Active view: "list", "schedule", "feedback", "decline"
  const [view, setView] = useState("list");
  const [selectedInterview, setSelectedInterview] = useState(null);

  // Schedule form state
  const [interviewType, setInterviewType] = useState("rh");
  const [scheduledAt, setScheduledAt] = useState("");
  const [timezone, setTimezone] = useState("Europe/Paris");
  const [durationMinutes, setDurationMinutes] = useState(45);
  const [locationOrLink, setLocationOrLink] = useState("");
  const [flexibilityNotes, setFlexibilityNotes] = useState("");
  const [participants, setParticipants] = useState([]);
  const [partName, setPartName] = useState("");
  const [partEmail, setPartEmail] = useState("");

  // Feedback form state
  const [feedbackRating, setFeedbackRating] = useState("favorable");
  const [feedbackRecommendation, setFeedbackRecommendation] = useState("advance");
  const [feedbackStrengths, setFeedbackStrengths] = useState("");
  const [feedbackVigilance, setFeedbackVigilance] = useState("");
  const [feedbackComments, setFeedbackComments] = useState("");
  const [isDelegated, setIsDelegated] = useState(false);
  const [delegatedByName, setDelegatedByName] = useState("");
  const [delegatedByRole, setDelegatedByRole] = useState("Directeur Général");
  const [validationMode, setValidationMode] = useState("oral");

  // Decline form state
  const [declineReason, setDeclineReason] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load interviews
  const loadInterviews = useCallback(async () => {
    if (!applicationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await listApplicationInterviews(applicationId);
      setInterviews(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erreur de récupération des entretiens"));
      logAppError("InterviewsWorkflowModal.loadInterviews", err);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    if (isOpen) {
      setView("list");
      setSelectedInterview(null);
      setActionSuccess(null);
      setError(null);
      void loadInterviews();
    }
  }, [isOpen, loadInterviews]);

  const handleAddParticipant = () => {
    if (!partEmail || !partEmail.includes("@")) return;
    setParticipants((prev) => [
      ...prev,
      { name: partName.trim() || partEmail.split("@")[0], email: partEmail.trim().toLowerCase() },
    ]);
    setPartName("");
    setPartEmail("");
  };

  const handleRemoveParticipant = (idx) => {
    setParticipants((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduledAt) {
      setError("Veuillez renseigner la date et l'heure de l'entretien");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const isoUtc = new Date(scheduledAt).toISOString();
      const payload = {
        type: interviewType,
        scheduled_at: isoUtc,
        timezone,
        duration_minutes: parseInt(durationMinutes, 10) || 45,
        location_or_link: locationOrLink,
        participants,
        flexibility_notes: flexibilityNotes,
      };

      await createApplicationInterview(applicationId, payload);

      setActionSuccess("Entretien planifié avec succès");
      await loadInterviews();
      setView("list");
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, "Échec de planification de l'entretien"));
      logAppError("InterviewsWorkflowModal.handleScheduleSubmit", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInterview) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await declineInterview(selectedInterview.interview_id, {
        reason: declineReason || "Refus notifié par le participant/candidat",
      });

      setActionSuccess("Refus d'invitation enregistré et équipe alertée");
      await loadInterviews();
      setView("list");
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, "Échec de notification de refus"));
      logAppError("InterviewsWorkflowModal.handleDeclineSubmit", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInterview) return;

    if (isDelegated && !delegatedByName.trim()) {
      setError("Veuillez renseigner le nom du directeur pour la saisie par délégation.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const payload = {
        rating: feedbackRating,
        recommendation: feedbackRecommendation,
        strengths: feedbackStrengths ? feedbackStrengths.split("\n").filter(Boolean) : [],
        vigilance_points: feedbackVigilance ? feedbackVigilance.split("\n").filter(Boolean) : [],
        comments: feedbackComments,
        is_delegated: isDelegated,
        delegated_by_name: isDelegated ? delegatedByName.trim() : null,
        delegated_by_role: isDelegated ? delegatedByRole.trim() : null,
        validation_mode: isDelegated ? validationMode : null,
      };

      await submitInterviewFeedback(selectedInterview.interview_id, payload);

      setActionSuccess("Compte rendu enregistré avec traçabilité complète");
      await loadInterviews();
      setView("list");
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, "Échec d'enregistrement du compte rendu"));
      logAppError("InterviewsWorkflowModal.handleFeedbackSubmit", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 px-6 py-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Entretiens Candidat & Suivi</h2>
              <p className="text-xs text-indigo-200">
                Mode opérationnel V1 (Teams manuel & Fuseaux horaires) • Score IA indépendant
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Global Alert / Status */}
        {actionSuccess && (
          <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{actionSuccess}</span>
          </div>
        )}
        {error && (
          <div className="bg-rose-50 border-b border-rose-200 px-6 py-3 text-sm text-rose-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="p-6 max-h-[75vh] overflow-y-auto space-y-6">
          {/* VIEW: LIST */}
          {view === "list" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Cycle des entretiens ({interviews.length})
                  </h3>
                  <p className="text-xs text-slate-500">
                    Chaque entretien dispose d'un cycle de vie propre et d'une traçabilité des comptes rendus.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setScheduledAt("");
                    setLocationOrLink("");
                    setFlexibilityNotes("");
                    setParticipants([]);
                    setView("schedule");
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-1.5 shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Planifier un entretien
                </button>
              </div>

              {isLoading ? (
                <div className="py-12 flex flex-col items-center justify-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mb-2" />
                  <p className="text-xs">Chargement des entretiens...</p>
                </div>
              ) : interviews.length === 0 ? (
                <div className="py-8 text-center bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                  <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600">Aucun entretien enregistré</p>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto mt-1">
                    Planifiez un entretien RH, technique ou client pour assurer le suivi du candidat.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {interviews.map((item) => (
                    <div
                      key={item.interview_id}
                      className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors bg-white shadow-sm"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-slate-900 capitalize">
                              {item.type}
                            </span>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                item.status === "completed"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : item.status === "declined"
                                  ? "bg-rose-100 text-rose-800"
                                  : item.status === "scheduled"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {item.status}
                            </span>
                            <span className="text-xs text-slate-400">
                              Zone: {item.timezone || "Europe/Paris"}
                            </span>
                          </div>
                          <div className="text-xs text-slate-600 flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              {item.scheduled_at
                                ? new Date(item.scheduled_at).toLocaleString("fr-FR", {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                  })
                                : "Date à définir"}{" "}
                              ({item.duration_minutes || 45} min)
                            </span>
                            {item.location_or_link && (
                              <span className="flex items-center gap-1 text-indigo-600 truncate max-w-xs">
                                <Video className="w-3.5 h-3.5" />
                                {item.location_or_link}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions for this interview */}
                        <div className="flex items-center gap-1.5">
                          {item.status !== "completed" && item.status !== "declined" && (
                            <button
                              onClick={() => {
                                setSelectedInterview(item);
                                setDeclineReason("");
                                setView("decline");
                              }}
                              className="px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-1"
                              title="Déclarer un refus d'invitation"
                            >
                              <XCircle className="w-3.5 h-3.5 text-rose-500" />
                              Refus
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedInterview(item);
                              if (item.feedback) {
                                setFeedbackRating(item.feedback.rating || "favorable");
                                setFeedbackRecommendation(item.feedback.recommendation || "advance");
                                setFeedbackStrengths((item.feedback.strengths || []).join("\n"));
                                setFeedbackVigilance((item.feedback.vigilance_points || []).join("\n"));
                                setFeedbackComments(item.feedback.comments || "");
                                setIsDelegated(!!item.feedback.is_delegated);
                                setDelegatedByName(item.feedback.delegated_by_name || "");
                                setDelegatedByRole(item.feedback.delegated_by_role || "Directeur Général");
                                setValidationMode(item.feedback.validation_mode || "oral");
                              } else {
                                setFeedbackRating("favorable");
                                setFeedbackRecommendation("advance");
                                setFeedbackStrengths("");
                                setFeedbackVigilance("");
                                setFeedbackComments("");
                                setIsDelegated(false);
                                setDelegatedByName("");
                              }
                              setView("feedback");
                            }}
                            className="px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-1"
                          >
                            <FileText className="w-3.5 h-3.5 text-indigo-500" />
                            {item.feedback ? "Modifier CR" : "Saisir CR"}
                          </button>
                        </div>
                      </div>

                      {/* Display Feedback summary if exists */}
                      {item.feedback && (
                        <div className="mt-3 pt-3 border-t border-slate-100 bg-slate-50/70 rounded-lg p-3 text-xs space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                              Compte rendu qualitatif
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded border font-medium ${
                                  RATINGS.find((r) => r.value === item.feedback.rating)?.color ||
                                  "bg-slate-100"
                                }`}
                              >
                                {item.feedback.rating}
                              </span>
                            </span>
                            <span className="text-slate-400 text-[11px]">
                              Par {item.feedback.author_name}
                              {item.feedback.is_delegated && (
                                <span className="text-indigo-600 font-medium ml-1">
                                  (délégation {item.feedback.delegated_by_name})
                                </span>
                              )}
                            </span>
                          </div>
                          {item.feedback.comments && (
                            <p className="text-slate-600 italic">"{item.feedback.comments}"</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* VIEW: SCHEDULE */}
          {view === "schedule" && (
            <form onSubmit={handleScheduleSubmit} className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  Planifier un nouvel entretien
                </h3>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="text-xs text-slate-500 hover:text-slate-800"
                >
                  ← Retour à la liste
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Type d'entretien
                  </label>
                  <select
                    value={interviewType}
                    onChange={(e) => setInterviewType(e.target.value)}
                    className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {INTERVIEW_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Fuseau horaire de référence
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Date et heure de début
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    required
                    className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Durée estimée (minutes)
                  </label>
                  <input
                    type="number"
                    min="15"
                    step="15"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Lien Teams ou lieu de rencontre
                </label>
                <input
                  type="text"
                  placeholder="https://teams.microsoft.com/l/meetup-join/... ou Salle A Casablanca"
                  value={locationOrLink}
                  onChange={(e) => setLocationOrLink(e.target.value)}
                  className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              {/* Participants */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Participants convoqués
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Nom du participant"
                    value={partName}
                    onChange={(e) => setPartName(e.target.value)}
                    className="w-1/3 text-xs rounded-lg border-slate-300 shadow-sm"
                  />
                  <input
                    type="email"
                    placeholder="email@lenvia.com"
                    value={partEmail}
                    onChange={(e) => setPartEmail(e.target.value)}
                    className="flex-1 text-xs rounded-lg border-slate-300 shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddParticipant}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg border border-slate-300"
                  >
                    Ajouter
                  </button>
                </div>

                {participants.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200">
                    {participants.map((p, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-200 text-xs text-slate-700 rounded-md"
                      >
                        <Users className="w-3 h-3 text-slate-400" />
                        {p.name} ({p.email})
                        <button
                          type="button"
                          onClick={() => handleRemoveParticipant(idx)}
                          className="text-slate-400 hover:text-rose-500 ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Commentaire de flexibilité horaire (créneaux de repli)
                </label>
                <textarea
                  rows={2}
                  value={flexibilityNotes}
                  onChange={(e) => setFlexibilityNotes(e.target.value)}
                  placeholder="Ex: Flexible le matin ou en fin d'après-midi, prévenir 24h à l'avance."
                  className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p>
                  <strong>Mode manuel V1 actif :</strong> Les dates sont conservées en UTC et restituées
                  dans le fuseau de référence choisi. Aucun token Microsoft Graph n'est requis.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                  Valider la planification
                </button>
              </div>
            </form>
          )}

          {/* VIEW: DECLINE */}
          {view === "decline" && selectedInterview && (
            <form onSubmit={handleDeclineSubmit} className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-rose-800 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-rose-600" />
                  Enregistrer un refus d'invitation
                </h3>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="text-xs text-slate-500 hover:text-slate-800"
                >
                  ← Retour à la liste
                </button>
              </div>

              <p className="text-xs text-slate-600">
                Un participant ou le candidat a décliné le créneau prévu pour l'entretien du{" "}
                <strong>
                  {new Date(selectedInterview.scheduled_at).toLocaleString("fr-FR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </strong>
                . Le système passera le statut à <code>declined</code> et alertera immédiatement l'équipe.
              </p>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Motif du refus notifié
                </label>
                <textarea
                  rows={3}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Ex: Conflit d'agenda imprévu, indisponible avant la semaine prochaine."
                  required
                  className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                  Enregistrer le refus et alerter l'équipe
                </button>
              </div>
            </form>
          )}

          {/* VIEW: FEEDBACK */}
          {view === "feedback" && selectedInterview && (
            <form onSubmit={handleFeedbackSubmit} className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  Compte rendu de l'entretien ({selectedInterview.type})
                </h3>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="text-xs text-slate-500 hover:text-slate-800"
                >
                  ← Retour à la liste
                </button>
              </div>

              {/* Notice regarding AI matching score independence */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <p>
                  <strong>Indépendance stricte de l'évaluation humaine :</strong> Ce compte rendu
                  qualitatif trace l'appréciation des évaluateurs. En conformité avec la gouvernance LENVIA,
                  il ne modifie ni n'écrase jamais le score de matching IA du besoin.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Évaluation qualitative globale
                  </label>
                  <select
                    value={feedbackRating}
                    onChange={(e) => setFeedbackRating(e.target.value)}
                    className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {RATINGS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Recommandation décisionnelle
                  </label>
                  <select
                    value={feedbackRecommendation}
                    onChange={(e) => setFeedbackRecommendation(e.target.value)}
                    className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="advance">Poursuivre le processus</option>
                    <option value="hold">Mettre en vivier / En attente</option>
                    <option value="reject">Refuser la candidature</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Points forts observés (1 par ligne)
                </label>
                <textarea
                  rows={2}
                  value={feedbackStrengths}
                  onChange={(e) => setFeedbackStrengths(e.target.value)}
                  placeholder="Ex: Excellente maîtrise de React&#10;Très bonne communication en français et anglais"
                  className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Points de vigilance (1 par ligne)
                </label>
                <textarea
                  rows={2}
                  value={feedbackVigilance}
                  onChange={(e) => setFeedbackVigilance(e.target.value)}
                  placeholder="Ex: Moins à l'aise sur les architectures distribuées&#10;Préavis de 3 mois"
                  className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Commentaires détaillés
                </label>
                <textarea
                  rows={3}
                  value={feedbackComments}
                  onChange={(e) => setFeedbackComments(e.target.value)}
                  placeholder="Synthèse de l'échange..."
                  className="w-full text-xs rounded-lg border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              {/* Delegation Sub-form */}
              <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDelegated}
                    onChange={(e) => setIsDelegated(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-medium text-slate-700">
                    Saisie réalisée par délégation pour un Directeur
                  </span>
                </label>

                {isDelegated && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Nom du Directeur
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Ex: M. Khalid El Amrani"
                        value={delegatedByName}
                        onChange={(e) => setDelegatedByName(e.target.value)}
                        className="w-full text-xs rounded-lg border-slate-300 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Fonction
                      </label>
                      <input
                        type="text"
                        value={delegatedByRole}
                        onChange={(e) => setDelegatedByRole(e.target.value)}
                        className="w-full text-xs rounded-lg border-slate-300 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Mode de validation
                      </label>
                      <select
                        value={validationMode}
                        onChange={(e) => setValidationMode(e.target.value)}
                        className="w-full text-xs rounded-lg border-slate-300 shadow-sm"
                      >
                        <option value="oral">Accord oral</option>
                        <option value="email">Confirmation par email</option>
                        <option value="reunion">Débriefing en réunion</option>
                      </select>
                    </div>
                    <p className="text-[10px] text-slate-500 col-span-3">
                      L'auteur réel de la saisie (votre compte) et l'accord du directeur sont tous deux
                      enregistrés sans aucune usurpation d'identité.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  Enregistrer le compte rendu
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
